/**
 * A small writer for AutoCAD R12 ASCII DXF.
 *
 * DXF is a published format, so there is no SDK and no licence to worry about, and R12 is the
 * dialect every CAD program still reads. The file is a flat list of group codes: an even code on one
 * line, its value on the next. Only what a classification drawing needs is here — layers, lines,
 * polylines, circles, arcs and text — and nothing is emitted that a reader could choke on.
 */

export type Point = [number, number];

export interface Layer {
  name: string;
  /** AutoCAD colour index: 1 red, 2 yellow, 3 green, 5 blue, 6 magenta, 7 default, 8 grey, 30 orange. */
  colour: number;
}

export type Entity =
  | { kind: 'line'; layer: string; from: Point; to: Point }
  | { kind: 'polyline'; layer: string; points: Point[]; closed: boolean }
  | { kind: 'circle'; layer: string; centre: Point; radius: number }
  | { kind: 'arc'; layer: string; centre: Point; radius: number; startDeg: number; endDeg: number }
  | { kind: 'text'; layer: string; at: Point; height: number; text: string; align?: 'left' | 'centre' | 'right' };

/**
 * Layer names in R12 may hold letters, digits and `$-_` only, and are conventionally upper case.
 * Anything else becomes an underscore so a project name can never produce a file that will not open.
 */
export function layerName(raw: string): string {
  const cleaned = raw.toUpperCase().replace(/[^A-Z0-9$_-]/g, '_').replace(/_+/g, '_').replace(/^_|_$/g, '');
  return cleaned.slice(0, 31) || 'LAYER';
}

/** R12 text is a single ANSI line; anything else is folded away rather than written raw. */
export function dxfText(raw: string): string {
  return raw.replace(/[\r\n\t]+/g, ' ').replace(/[^\x20-\x7E]/g, (c) => SUBSTITUTES[c] ?? '?').slice(0, 250);
}

const SUBSTITUTES: Record<string, string> = {
  '°': 'deg', '³': '3', '²': '2', '·': '.', '–': '-', '—': '-', '’': "'", '‘': "'", '“': '"', '”': '"', 'µ': 'u', '×': 'x', '≤': '<=', '≥': '>=',
};

const num = (v: number): string => {
  if (!Number.isFinite(v)) return '0.0';
  const s = v.toFixed(6).replace(/0+$/, '').replace(/\.$/, '.0');
  return s === '-0.0' ? '0.0' : s;
};

class Out {
  private readonly parts: string[] = [];
  pair(code: number, value: string | number): void {
    this.parts.push(String(code), typeof value === 'number' ? num(value) : value);
  }
  int(code: number, value: number): void { this.parts.push(String(code), String(Math.round(value))); }
  toString(): string { return this.parts.join('\r\n') + '\r\n'; }
}

function writeEntity(o: Out, e: Entity): void {
  // sanitised here, so the table and the entities can never disagree about a name
  if (e.kind === 'line') {
    o.pair(0, 'LINE'); o.pair(8, layerName(e.layer));
    o.pair(10, e.from[0]); o.pair(20, e.from[1]); o.pair(30, 0);
    o.pair(11, e.to[0]); o.pair(21, e.to[1]); o.pair(31, 0);
    return;
  }
  if (e.kind === 'polyline') {
    // R12 has no LWPOLYLINE: a POLYLINE owns VERTEX records and is ended by SEQEND
    o.pair(0, 'POLYLINE'); o.pair(8, layerName(e.layer)); o.int(66, 1);
    o.pair(10, 0); o.pair(20, 0); o.pair(30, 0);
    o.int(70, e.closed ? 1 : 0);
    for (const p of e.points) {
      o.pair(0, 'VERTEX'); o.pair(8, layerName(e.layer));
      o.pair(10, p[0]); o.pair(20, p[1]); o.pair(30, 0);
    }
    o.pair(0, 'SEQEND'); o.pair(8, layerName(e.layer));
    return;
  }
  if (e.kind === 'circle') {
    o.pair(0, 'CIRCLE'); o.pair(8, layerName(e.layer));
    o.pair(10, e.centre[0]); o.pair(20, e.centre[1]); o.pair(30, 0);
    o.pair(40, e.radius);
    return;
  }
  if (e.kind === 'arc') {
    o.pair(0, 'ARC'); o.pair(8, layerName(e.layer));
    o.pair(10, e.centre[0]); o.pair(20, e.centre[1]); o.pair(30, 0);
    o.pair(40, e.radius); o.pair(50, e.startDeg); o.pair(51, e.endDeg);
    return;
  }
  o.pair(0, 'TEXT'); o.pair(8, layerName(e.layer));
  o.pair(10, e.at[0]); o.pair(20, e.at[1]); o.pair(30, 0);
  o.pair(40, e.height); o.pair(1, dxfText(e.text));
  if (e.align && e.align !== 'left') {
    o.int(72, e.align === 'centre' ? 1 : 2);
    // with an alignment other than left, the second point is the one that counts
    o.pair(11, e.at[0]); o.pair(21, e.at[1]); o.pair(31, 0);
  }
}

export interface DxfDocument {
  layers: Layer[];
  entities: Entity[];
}

export function writeDxf({ layers, entities }: DxfDocument): string {
  const o = new Out();
  const xs = entities.flatMap(pointsOf).map((p) => p[0]);
  const ys = entities.flatMap(pointsOf).map((p) => p[1]);
  const min: Point = [xs.length ? Math.min(...xs) : 0, ys.length ? Math.min(...ys) : 0];
  const max: Point = [xs.length ? Math.max(...xs) : 0, ys.length ? Math.max(...ys) : 0];

  o.pair(0, 'SECTION'); o.pair(2, 'HEADER');
  o.pair(9, '$ACADVER'); o.pair(1, 'AC1009');
  // 6 = metres, so a reader knows one drawing unit is one metre
  o.pair(9, '$INSUNITS'); o.int(70, 6);
  o.pair(9, '$EXTMIN'); o.pair(10, min[0]); o.pair(20, min[1]); o.pair(30, 0);
  o.pair(9, '$EXTMAX'); o.pair(10, max[0]); o.pair(20, max[1]); o.pair(30, 0);
  o.pair(0, 'ENDSEC');

  o.pair(0, 'SECTION'); o.pair(2, 'TABLES');
  o.pair(0, 'TABLE'); o.pair(2, 'LTYPE'); o.int(70, 1);
  o.pair(0, 'LTYPE'); o.pair(2, 'CONTINUOUS'); o.int(70, 0); o.pair(3, 'Solid line'); o.int(72, 65); o.int(73, 0); o.pair(40, 0);
  o.pair(0, 'ENDTAB');

  o.pair(0, 'TABLE'); o.pair(2, 'LAYER'); o.int(70, layers.length);
  for (const l of layers) {
    o.pair(0, 'LAYER'); o.pair(2, layerName(l.name)); o.int(70, 0); o.int(62, l.colour); o.pair(6, 'CONTINUOUS');
  }
  o.pair(0, 'ENDTAB');

  // a TEXT entity names a style, so the standard one has to exist
  o.pair(0, 'TABLE'); o.pair(2, 'STYLE'); o.int(70, 1);
  o.pair(0, 'STYLE'); o.pair(2, 'STANDARD'); o.int(70, 0); o.pair(40, 0); o.pair(41, 1); o.pair(50, 0); o.int(71, 0); o.pair(42, 2.5); o.pair(3, 'txt'); o.pair(4, '');
  o.pair(0, 'ENDTAB');
  o.pair(0, 'ENDSEC');

  o.pair(0, 'SECTION'); o.pair(2, 'ENTITIES');
  for (const e of entities) writeEntity(o, e);
  o.pair(0, 'ENDSEC');
  o.pair(0, 'EOF');
  return o.toString();
}

function pointsOf(e: Entity): Point[] {
  if (e.kind === 'line') return [e.from, e.to];
  if (e.kind === 'polyline') return e.points;
  if (e.kind === 'text') return [e.at];
  return [[e.centre[0] - e.radius, e.centre[1] - e.radius], [e.centre[0] + e.radius, e.centre[1] + e.radius]];
}
